"use client";

import React, { useId, useState } from "react";
import { useTranslations } from "next-intl";

import { useLoginMutation } from "@/api/auth/authPublicApi";
import {
  matchFullname,
  useChangePasswordViaFullnameMutation,
  useCheckAccountByFullnameMutation,
  type FullName,
} from "@/api/register/accountRecoveryApi";
import { EyeIcon, EyeOffIcon } from "@/components/icons";
import { firstFieldMessage, getErrorBag } from "@/lib/apiErrorBag";
import { cn } from "@/src/lib/utils";

const INPUT_CLASS =
  "w-full p-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent";
const PRIMARY_BUTTON_CLASS =
  "cursor-pointer flex-1 py-3 rounded-lg font-medium text-white bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-700 hover:to-purple-700 disabled:from-gray-400 disabled:to-gray-500 disabled:cursor-not-allowed transition-all shadow-md";
const SECONDARY_BUTTON_CLASS =
  "cursor-pointer flex-1 py-3 rounded-lg font-medium text-blue-600 border-2 border-blue-600 hover:bg-blue-50 transition-all shadow-md";

type Props = {
  /** Логин, на который request-via-code ответил verification_type=fullname. */
  username: string;
  /** Назад к форме входа. */
  onBack: () => void;
  /** Пароль сменён и вход выполнен — токены уже разложены login-мутацией. */
  onLoggedIn: () => void;
};

/**
 * Продолжение «Восстановления пароля» в окне входа для аккаунтов без телефона и почты.
 *
 * Так заводятся ученики олимпиад (Алматы, Астана дарыны): логин — ИИН, и request-via-code
 * отвечает verification_type=fullname, ничего не отправляя. Личность подтверждаем фамилией
 * и именем, как диалог на странице регистрации (ForgotPasswordDialog): check-via-fullname
 * в обоих порядках → change-via-fullname → вход.
 *
 * Бэк сверяет Ф.И.О. буква в букву и только у аккаунтов с phone и email = NULL. Пустая строка
 * в phone для request-via-code тоже «нет контакта», а для сверки — нет: такой аккаунт получит
 * «не совпало». Чинится на бэке.
 */
export default function FullnamePasswordReset({ username, onBack, onLoggedIn }: Props) {
  const t = useTranslations();

  const [lastname, setLastname] = useState("");
  const [firstname, setFirstname] = useState("");
  /* Порядок имён, на котором бэк подтвердил аккаунт: с ним же и меняем пароль. */
  const [matched, setMatched] = useState<FullName | null>(null);
  const [password, setPassword] = useState("");
  const [confirmation, setConfirmation] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const errorId = useId();
  const describedBy = error ? errorId : undefined;

  const [checkByFullname] = useCheckAccountByFullnameMutation();
  const [changeViaFullname] = useChangePasswordViaFullnameMutation();
  const [login] = useLoginMutation();

  const passwordsDiffer = !!confirmation && confirmation !== password;
  const canSubmit = matched
    ? !!password && password === confirmation
    : !!lastname.trim() && !!firstname.trim();

  const verifyIdentity = async () => {
    setBusy(true);
    setError(null);

    try {
      const names = await matchFullname(
        (candidate) => checkByFullname({ username, ...candidate }).unwrap(),
        { firstname, lastname },
      );

      if (names) setMatched(names);
      else setError(t("ui-forgot-password-mismatch"));
    } catch {
      setError(t("notification-request-reach-limit"));
    } finally {
      setBusy(false);
    }
  };

  const savePassword = async (names: FullName) => {
    setBusy(true);
    setError(null);

    try {
      await changeViaFullname({ username, ...names, password }).unwrap();
    } catch (e: unknown) {
      /* Правила пароля проверяет бэк — его формулировка точнее нашей. */
      const bag = getErrorBag(e);
      setError((bag && firstFieldMessage(bag)) || t("error-unknown-error"));
      setBusy(false);
      return;
    }

    try {
      await login({ username, password }).unwrap();
    } catch {
      /* Пароль уже сменён: «ошибка» здесь врала бы — отправляем входить вручную. */
      setError(t("ui-forgot-password-login-failed"));
      setBusy(false);
      return;
    }

    /* Форма остаётся занятой до перехода: повторный вход выдал бы новый токен, а бэк
       отзывает прежние — кабинет открылся бы с уже отозванным. */
    onLoggedIn();
  };

  const handleSubmit = (event: React.FormEvent) => {
    event.preventDefault();
    if (busy || !canSubmit) return;

    if (matched) void savePassword(matched);
    else void verifyIdentity();
  };

  return (
    <form className="space-y-4" onSubmit={handleSubmit}>
      <p className="text-sm text-gray-500 text-center">
        {matched ? t("ui-forgot-password-new-password") : t("ui-forgot-password-text")}
      </p>

      {error && (
        <p id={errorId} role="alert" className="text-sm text-red-600">
          {error}
        </p>
      )}

      {matched ? (
        <>
          <div className="relative">
            <input
              type={showPassword ? "text" : "password"}
              autoComplete="new-password"
              placeholder={t("label-new-password")}
              aria-label={t("label-new-password")}
              className={cn(INPUT_CLASS, "pr-10")}
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              disabled={busy}
              required
              aria-describedby={describedBy}
            />
            <button
              type="button"
              onClick={() => setShowPassword((v) => !v)}
              className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-500 cursor-pointer"
              aria-label={showPassword ? t("ui-hide-password") : t("ui-show-password")}
              disabled={busy}
            >
              {showPassword ? <EyeOffIcon size={20} /> : <EyeIcon size={20} />}
            </button>
          </div>

          <input
            type={showPassword ? "text" : "password"}
            autoComplete="new-password"
            placeholder={t("ui-forgot-password-repeat")}
            aria-label={t("ui-forgot-password-repeat")}
            className={cn(INPUT_CLASS, passwordsDiffer && "border-red-500 focus:ring-red-500")}
            value={confirmation}
            onChange={(e) => setConfirmation(e.target.value)}
            disabled={busy}
            required
            aria-describedby={describedBy}
            aria-invalid={passwordsDiffer}
          />

          <p className="text-xs text-gray-500">
            {passwordsDiffer
              ? t("error-passwords-do-not-match")
              : t("ui-forgot-password-remember")}
          </p>
        </>
      ) : (
        <>
          <input
            type="text"
            autoComplete="family-name"
            placeholder={t("label-lastname-participants")}
            aria-label={t("label-lastname-participants")}
            className={INPUT_CLASS}
            value={lastname}
            onChange={(e) => setLastname(e.target.value)}
            disabled={busy}
            required
            aria-describedby={describedBy}
          />
          <input
            type="text"
            autoComplete="given-name"
            placeholder={t("label-firstname-participants")}
            aria-label={t("label-firstname-participants")}
            className={INPUT_CLASS}
            value={firstname}
            onChange={(e) => setFirstname(e.target.value)}
            disabled={busy}
            required
            aria-describedby={describedBy}
          />
        </>
      )}

      <div className="flex gap-3 pt-2">
        <button type="submit" disabled={busy || !canSubmit} className={PRIMARY_BUTTON_CLASS}>
          {busy
            ? t("label-loading")
            : matched
              ? t("ui-forgot-password-save")
              : t("ui-next")}
        </button>

        <button type="button" onClick={onBack} disabled={busy} className={SECONDARY_BUTTON_CLASS}>
          {t("btn-entrace")}
        </button>
      </div>
    </form>
  );
}
